import React, { Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { shape, func, array, bool, string } from 'prop-types';
import HelpIcon from '@material-ui/icons/Help';
import WarningIcon from '@material-ui/icons/Warning';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import forEach from 'lodash/forEach';
import trim from 'lodash/trim';
import {
  Button,
  Grid,
  Link,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';

import RoleInfoDialogContent from '../RoleInfoDialogContent';
import LookupByEmailDialog from './LookupByEmailDialog';
import { getI18n } from '../../../../utils/intl';
import {
  AlertDialog,
  Footer,
  FormSelect,
  FormTextField,
} from '../../../../components';
import { fetchUsers } from '../../../../actions/user';
import { getIsLoading, getUserListResults } from '../../../../reducers/user';
import styles from './styles';
import {
  ADMIN_USER_ROLES,
  ORG_USER_ROLES,
  ROLE_ORG_ADMIN,
  ROLE_PORTAL_ADMIN,
  ROLE_NAME_UUID_MAP,
  USER_ROLE_NAME_MAP,
  USER_TYPE_GLOBAL,
  USER_TYPE_LIST,
} from '../../../../constants';
import {
  deduceUserRoleAndAuthorizationType,
} from '../utils';

export const AddUser = (props) => {
  const {
    classes,
    authSchemes,
    userFormObject,
    updateUserFormObject,
    updateUserFormObjectMultipeFields,
    saveClicked,
    currentUserRole,
    userActiveOrg,
    currentUserOrg,
    userContext,
    fetchUsers,
    isExecutingUserSearchQuery,
    userSearchResults = [],
  } = props;

  const {
    authConfigUuid,
    email,
    firstName,
    lastName,
    userIsGuestOrOtherOrgUser,
    username,
  } = userFormObject;

  const intl = getI18n(useIntl());

  const [invalidEmail, setInvalidEmail] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState('');
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  const emailIsValid = (email) => /\S+@\S+\.\S{2,}/.test(email);

  const validateEmail = (value) => {
    let helperErrorText;
    if(value === '') {
      helperErrorText = intl.getI18nMessage('label.user.profile.email.mandatory');
    } else if (!emailIsValid(value)) {
      helperErrorText = intl.getI18nMessage('label.user.profile.email.format.error');
    }
    setInvalidEmail(helperErrorText);
    setEmailErrorText(helperErrorText);
    updateUserFormObject('email', value);
  }

  const TooltipIconForIdp = () => (
    <div className={classes.helpIconContainer}>
      <div>
        {intl.getI18nMessage('label.user.details.idp.dropdown.title')}
      </div>
      <Tooltip classes={classes} title={intl.getI18nMessage('label.user.details.idp.tooltip')} arrow placement="right">
        <HelpIcon className={classes.helpIcon}/>
      </Tooltip>
    </div>
  );

  const formatUserInfoLink = () =>
    (<Link underline = 'none' onClick={() => setShowRoleInfo(true)} className={classes.userRolesLink}>
      {intl.getI18nMessage('label.user.details.userrole.helptext.linktext')}
    </Link>);

  useEffect(() => {
    if(saveClicked) {
      validateEmail(email);
    }
  }, [saveClicked]);

  const [isAddingPortalAuthSchemeUser, setIsAddingPortalAuthSchemeUser] = useState(false);

  useEffect(()=> {
    const { portalAdmin, isIdpUser } = userContext;
    if (portalAdmin) {
      setIsAddingPortalAuthSchemeUser(authConfigUuid === 'DEFAULT');
    } else {
      setIsAddingPortalAuthSchemeUser(!isIdpUser);
    }
  }, [userContext, authConfigUuid]);

  const [isUserNameVerified] = useState(true);

  const renderAddPortalUserDetails = () =>
    (<Fragment>
      <FormTextField
        id="user-email"
        name={intl.getI18nMessage('label.user.details.email.title')}
        helperText={invalidEmail? emailErrorText :intl.getI18nMessage('label.user.details.textfields.helptext')}
        value={email}
        handleChange={val => validateEmail(val)}
        error={invalidEmail}
      />
      <FormTextField
        id="user-first-name"
        name={intl.getI18nMessage('label.user.details.firstname.title')}
        helperText={intl.getI18nMessage('label.user.details.textfields.helptext')}
        value={firstName}
        handleChange={val => updateUserFormObject('firstName', val)}
        optional
      />
      <FormTextField
        id="user-last-name"
        name={intl.getI18nMessage('label.user.details.lastname.title')}
        helperText={intl.getI18nMessage('label.user.details.textfields.helptext')}
        value={lastName}
        handleChange={val => updateUserFormObject('lastName', val)}
        optional
      />
    </Fragment>);

  const [seletedUserUUID, setSelectedUserUUID] = useState('');

  const [showEmailSearchDialog, setShowEmailSearchDialog] = useState(false);
  const findViaEmail = () => {
    setShowEmailSearchDialog(true);
  };

  const [isCheckingUserName, setIsCheckingUserName] = useState(false);
  const [showVerificationComponent, setShowVerificationComponent] = useState(false);

  const checkIfUserNameExists = () => {
    if (!username) {
      return;
    }
    fetchUsers({
      authConfigUuid,
      username: trim(username),
      includeAnonymous: true,
      isExactMatch: true,
      organizationCheck: 'add',
    });
    setSelectedUserUUID('');
    setShowVerificationComponent(false);
    setIsCheckingUserName(true);
  };

  useEffect(() => {
    if (!isExecutingUserSearchQuery && isCheckingUserName) {
      const { portalAdmin } = userContext;
      const usernameExists = userSearchResults.length === 1;
      if (usernameExists) {
        const selectedUser = userSearchResults[0];
        if (portalAdmin) {
          setSelectedUserUUID(selectedUser.uuid);
          updateUserFormObject('userCanBeAdded', false);
        } else {
          const { access = [] } = selectedUser;
          if (access.length === 1) {
            setSelectedUserUUID(selectedUser.uuid);
            updateUserFormObject('userCanBeAdded', false);
          } else {
            updateUserFormObjectMultipeFields({
              userCanBeAdded: true,
              userIsGuestOrOtherOrgUser: true,
              userToBeAdded: {
                uuid: selectedUser.uuid,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                email: selectedUser.email,
                status: selectedUser.status,
                tenantId: selectedUser.tenantId,
              },
            });
          }
        }
      } else {
        updateUserFormObject('userCanBeAdded', true);
      }
      setShowVerificationComponent(true);
      setIsCheckingUserName(false);
    }
  }, [isCheckingUserName, isExecutingUserSearchQuery]);

  const renderVerificationComponent = () => {
    if (!seletedUserUUID) {
      return <div
          data-apim-test="verification-message"
          className={classes.verificationInfo}
        >
        <div className={classes.verificationIcon}>
          <InfoOutlinedIcon />
        </div>
        <div className={classes.verificationMsg}>
          {userIsGuestOrOtherOrgUser &&
            intl.getI18nMessage('label.user.details.idp.username.lookup.not.existing.other.org.user')
          }
          {!userIsGuestOrOtherOrgUser &&
            intl.getI18nMessage('label.user.details.idp.username.lookup.not.existing.user')
          }
        </div>
      </div>;
    } else {
      const { portalAdmin, uuid: loggedInUserId } = userContext;
      return (<div
          className={classes.verificationError}
          data-apim-test="verification-message"
        >
        <div className={classes.verificationIcon}>
          <WarningIcon />
        </div>
        <div className={classes.verificationMsg}>
          {portalAdmin && intl.getI18nMessage('label.user.details.idp.username.lookup.existing.user.portaladmin')}
          {!portalAdmin && intl.getI18nMessage('label.user.details.idp.username.lookup.existing.user.orgadmin')}
          {(seletedUserUUID !== loggedInUserId) && <Link underline="none" href={`/admin/console/users/details/${seletedUserUUID}/`}>
            &nbsp;{intl.getI18nMessage('label.user.details.idp.username.lookup.existing.user.link')}
          </Link>}
        </div>
      </div>);
    }
  };

  const renderAddIdpUserDetails = () =>
    (<Fragment>
      <Grid container className={classes.usernamerow}>
        <Grid item md={8} sm={8} xs={7}>
          <FormTextField
            id="user-name"
            name={intl.getI18nMessage('label.user.details.idp.username.title')}
            helperText={isUserNameVerified && intl.getI18nMessage('label.user.details.idp.username.not.verified.helptext')}
            handleBlur={checkIfUserNameExists}
            value={username}
            handleChange={val => updateUserFormObject('username', val)}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={5}>
          <Button
            id="find-via-email-button"
            data-apim-test="find-via-email-button"
            color="primary"
            variant="outlined"
            onMouseDown={findViaEmail}
            classes={{
              root: classes.checkusername,
            }}
          >
            {intl.getI18nMessage('label.user.details.find.via.email.title')}
          </Button>
        </Grid>
      </Grid>
      <div>
        <Button
          id="check-username-exists"
          data-apim-test="check-username-exists"
          color="primary"
          variant="text"
          onClick={checkIfUserNameExists}
          disabled={!username}
        >
          {intl.getI18nMessage('label.user.details.check.username.exists.title')}
        </Button>
      </div>
      {showVerificationComponent && renderVerificationComponent()}
    </Fragment>);

  const renderAddUserDetails = () => {
    if (isAddingPortalAuthSchemeUser) {
      return renderAddPortalUserDetails();
    }
    return renderAddIdpUserDetails();
  }

  const handleUserSearchDialogCancel = () => {
    setShowEmailSearchDialog(false);
  };

  const handleUserSearchDialogSubmit = (selectedUser) => {
    const { uuid } = selectedUser;
    const { portalAdmin } = userContext;
    if (portalAdmin) {
      setSelectedUserUUID(uuid);
      updateUserFormObjectMultipeFields({
        username: selectedUser.username,
        userCanBeAdded: false,
      });
    } else {
      const { access = [] } = selectedUser;
      if (access.length === 1) {
        setSelectedUserUUID(uuid);
        updateUserFormObjectMultipeFields({
          username: selectedUser.username,
          userCanBeAdded: false,
        });
      } else {
        updateUserFormObjectMultipeFields({
          userCanBeAdded: true,
          userIsGuestOrOtherOrgUser: true,
          username: selectedUser.username,
          userToBeAdded: {
            uuid: selectedUser.uuid,
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            email: selectedUser.email,
            status: selectedUser.status,
            tenantId: selectedUser.tenantId,
          },
        });
      }
    }
    setShowVerificationComponent(true);
    setShowEmailSearchDialog(false);
  };

  const { userType } = userFormObject;

  const roleSelectionOptions = userType === USER_TYPE_GLOBAL ? ADMIN_USER_ROLES : ORG_USER_ROLES;

  const {
    userRole,
  } = deduceUserRoleAndAuthorizationType(userFormObject.access, currentUserOrg, userType);

  const handleUserTypeChange = (val) => {
    updateUserFormObject('userType', val);
  };

  const handleUserRoleChange = (val) => {
    const oldAccess = userFormObject.access;
    const newAccess = [];
    if (userType === USER_TYPE_GLOBAL) {
      newAccess.push({
        roleUuid: ROLE_NAME_UUID_MAP[USER_ROLE_NAME_MAP[val]],
      });
    } else {
      forEach(oldAccess, item => {
        if (item.orgUuid !== currentUserOrg) {
          newAccess.push(item);
        } else {
          newAccess.push({
            orgUuid: currentUserOrg,
            roleUuid: ROLE_NAME_UUID_MAP[USER_ROLE_NAME_MAP[val]],
          });
        }
      });
    }
    updateUserFormObject('access', newAccess);
  };

  const noAuthSchemesAvailable = authSchemes.length === 0;

  return (
    <div
      id="user-details-add-user-container"
      data-apim-test="user-details-add-user-container"
    >
      <AlertDialog
        isOpen={showRoleInfo}
        title={intl.getI18nMessage('label.user.details.userroles.info.dialog.title')}
        component={
          <RoleInfoDialogContent isPortalAdmin={currentUserRole === ROLE_PORTAL_ADMIN} />
        }
        submitText={intl.getI18nMessage('label.user.details.userroles.info.dialog.close')}
        onClose={() => setShowRoleInfo(false)}
        onSubmit={() => setShowRoleInfo(false)}
        onCancel={() => setShowRoleInfo(false)}
        showCancel={false}
        dialogId={'proxy-status-error-dialog'}
      />
      <LookupByEmailDialog
        authConfigUuid={authConfigUuid}
        isOpen={showEmailSearchDialog}
        onCancel={handleUserSearchDialogCancel}
        onSubmit={handleUserSearchDialogSubmit}
        userContext={userContext}
      />
      <Grid container className={classes.marginPanelContainer}>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <Typography id="user-details-title" variant="h4">
            {intl.getI18nMessage('label.details')}
          </Typography>
          {currentUserRole === ROLE_PORTAL_ADMIN &&
            <FormSelect
              {...props}
              id="auth-config-uuid"
              data-apim-test="auth-config-uuid"
              noNoneOption
              noNativeSelect
              data={authSchemes}
              error={noAuthSchemesAvailable}
              name={(<TooltipIconForIdp />)}
              helperText={
                noAuthSchemesAvailable ?
                  intl.getI18nMessage('label.user.details.idp.dropdown.no.options.helptext')
                :
                  intl.getI18nMessage('label.user.details.idp.dropdown.helptext')
              }
              value={authConfigUuid}
              handleChange={val => updateUserFormObject('authConfigUuid', val)}
            />
          }
          {renderAddUserDetails()}
        </Grid>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <Typography id="user-roles-title" variant="h4">
            {intl.getI18nMessage('label.roles')}
          </Typography>
          {currentUserRole === ROLE_PORTAL_ADMIN &&
           <FormSelect
              {...props}
              id="user-type"
              data-apim-test="user-type"
              noNoneOption
              noNativeSelect
              name={intl.getI18nMessage('label.user.details.usertype.title')}
              data={USER_TYPE_LIST}
              helperText={intl.getI18nMessage('label.user.details.usertype.helptext')}
              value={userType}
              handleChange={val => handleUserTypeChange(val)}
            />
          }
          {(userType === USER_TYPE_GLOBAL ||
            currentUserRole === ROLE_ORG_ADMIN) &&
            <FormSelect
              {...props}
              id="user-role"
              data-apim-test="user-role"
              noNoneOption
              noNativeSelect
              name={intl.getI18nMessage('label.user.details.userrole.title')}
              data={roleSelectionOptions}
              helperText={''}
              value={userRole}
              handleChange={val => handleUserRoleChange(val)}
            />
          }
          <div>
            {intl.getI18nMessage('label.user.details.userrole.helptext', { link: formatUserInfoLink() })}
          </div>
          {currentUserRole === ROLE_ORG_ADMIN &&
            <FormTextField
              id="user-org"
              name={intl.getI18nMessage('label.user.details.organization.title')}
              value={userActiveOrg[0] && userActiveOrg[0].name}
              disabled
            />
          }
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

AddUser.propTypes = {
  authSchemes: array,
  classes: shape({}),
  currentUserOrg: string,
  currentUserRole: string,
  fetchUsers: func,
  isExecutingUserSearchQuery: bool,
  push: func,
  saveClicked: bool,
  updateUserFormObject: func,
  updateUserFormObjectMultipeFields: func,
  userActiveOrg: shape({}),
  userContext: shape({}),
  userFormObject: shape({}),
  userSearchResults: array,
};

const mapStateToProps = state => ({
  isExecutingUserSearchQuery: getIsLoading(state),
  userSearchResults: getUserListResults(state),
});

const mapDispatchToProps = {
  fetchUsers,
};

AddUser.displayName = 'AddUser';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(AddUser);
